import {
    Avatar,
    Badge,
    Button,
    Card,
    Group,
    Stack,
    Text,
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { ApplicationWithPlans } from '../types';
import { SubscriptionStatusInfo } from '../hooks/useSubscriptionStatus';
import { AiOutlineAppstore } from 'react-icons/ai';

const formatPrice = (amount: number) => `$${(amount / 100).toFixed(2)}`;

interface ApplicationCardProps {
    appWithPlans: ApplicationWithPlans;
    statusByProductId: Record<string, SubscriptionStatusInfo>;
}

export const ApplicationCard = ({ appWithPlans, statusByProductId }: ApplicationCardProps) => {
    const navigate = useNavigate();
    const { application, products, subscribedProductId } = appWithPlans;
    const isSubscribed = !!subscribedProductId;
    const subscribedInfo = subscribedProductId ? statusByProductId[subscribedProductId] : undefined;
    const subscribedProduct = subscribedProductId ? products.find(p => p.id === subscribedProductId) : undefined;

    const planCount = products.length;
    const lowestPrice = Math.min(...products.flatMap(p => p.prices?.items?.map(pr => pr.amount) ?? [Infinity]));

    return (
        <Card
            shadow="sm"
            padding="xl"
            radius="md"
            withBorder
            h="100%"
            style={isSubscribed ? { borderLeft: '4px solid var(--mantine-color-green-6)' } : undefined}
        >
            <Stack gap="md">
                <Group justify="space-between">
                    <Group gap="sm">
                        {application.logoUrl ? (
                            <Avatar src={application.logoUrl} size="md" radius="sm" />
                        ) : (
                            <Avatar size="md" radius="sm" color="blue">
                                <AiOutlineAppstore size="1.2em" />
                            </Avatar>
                        )}
                        <Text fw={700} size="xl">{application.name}</Text>
                    </Group>
                    {isSubscribed && <Badge color="green">Subscribed</Badge>}
                </Group>

                {application.description && (
                    <Text size="sm" c="dimmed" lineClamp={2}>{application.description}</Text>
                )}

                {isSubscribed && subscribedProduct && subscribedInfo?.amount != null ? (
                    <Text size="sm" fw={500} c="green">
                        {subscribedProduct.name} — {formatPrice(subscribedInfo.amount)} / {subscribedInfo.billingPeriod}
                    </Text>
                ) : lowestPrice < Infinity ? (
                    <Text size="sm" fw={500} c="dimmed">
                        From {formatPrice(lowestPrice)}/mo
                    </Text>
                ) : null}

                <Text size="xs" c="dimmed">{planCount} plan{planCount !== 1 ? 's' : ''} available</Text>

                <Group mt="md">
                    <Button
                        variant={isSubscribed ? 'filled' : 'light'}
                        onClick={() => navigate(`/products/app/${application.id}`)}
                    >
                        {isSubscribed ? 'Manage Plan' : 'View Plans'}
                    </Button>
                </Group>
            </Stack>
        </Card>
    );
};
